import { directSessionChunkBytes } from '~/universal/direct-upload-chunks';
import { getFileUploadProgress, setFileUploadProgress } from '~/shared/helpers/upload-progress-store';
import { getUploadTransport, initDirectBatch, uploadDirectFile } from '~/shared/helpers/direct-upload';
import { initUploadBatch } from '~/shared/helpers/init-upload-batch';
import { UPLOAD_CHUNK_BYTES, UPLOAD_PACKAGE_FILES, UPLOAD_PACKAGE_BYTES, VIDEO_UPLOAD_FORMATS, uploadSizeLimit } from '~/universal/upload-limits';
import paths from 'deepdash/paths';
import pickDeep from 'deepdash/pickDeep';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import set from 'lodash/set';
import mimeTypes from 'mime-types';
import { slugify } from 'transliteration';

import { MAX_MOBILE_SCREEN_SIZE } from '~/shared/constants';
import { completeUploadFile, uploadFile } from '~/shared/store/slices/files/thunks';

export const getFileExtension = (file) => {
  //images
  if (/image\/jpeg/.test(file)) {
    return 'jpeg';
  }

  if (/image\/jpg/.test(file)) {
    return 'jpg';
  }

  if (/image\/png/.test(file)) {
    return 'png';
  }

  if (/image\/gif/.test(file)) {
    return 'gif';
  }

  if (/image\/webp/.test(file)) {
    return 'webp';
  }

  //data sheet
  if (/application\/vnd\.ms-excel/.test(file)) {
    return 'xls';
  }

  if (/application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet/.test(file)) {
    return 'xlsx';
  }

  if (/text\/csv/.test(file)) {
    return 'csv';
  }

  //text
  if (/application\/pdf/.test(file)) {
    return 'pdf';
  }

  if (/text\//.test(file)) {
    return 'txt';
  }

  if (/application\/msword/.test(file)) {
    return 'doc';
  }

  if (/application\/vnd.openxmlformats-officedocument\.wordprocessingml\.document/.test(file)) {
    return 'docx';
  }

  //prezentation
  if (/application\/vnd\.ms-powerpoint/.test(file)) {
    return 'ppt';
  }

  if (/application\/vnd\.openxmlformats-officedocument\.presentationml\.presentation/.test(file)) {
    return 'pptx';
  }
};

const dataURLtoFile = (dataurl: string, filename: string = 'file') => {
  let arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[arr.length - 1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

export const readFileAsDataURL = async (file: File): Promise<string> => {
  if (!(file instanceof Blob)) {
    throw new Error('File is not available');
  }

  // Keep the original File/MIME. Rebuilding cloud-picked files from an ArrayBuffer
  // is unreliable in mobile Safari and used to strip the source MIME type.
  return window.URL.createObjectURL(file);
};

export const splitFileName = (fileName: string) => {
  return fileName?.match(/(.+?)(\.[^.]*$|$)/);
};

export const getCroppedImage = ({
  image,
  completedCrop,
  mimeType = 'image/webp',
  quality = 1,
}) => {
  if (!image || !completedCrop) {
    throw new Error('Crop does not exist');
  }

  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  // completedCrop is measured against the actual rendered <img> inside ReactCrop.
  // Use that same DOM image dimensions, otherwise coordinates drift when the preview is scaled by CSS.
  const renderedWidth = image.clientWidth || image.width || naturalWidth;
  const renderedHeight = image.clientHeight || image.height || naturalHeight;

  const scaleX = naturalWidth / renderedWidth;
  const scaleY = naturalHeight / renderedHeight;

  const sourceX = Math.max(0, Math.round(completedCrop.x * scaleX));
  const sourceY = Math.max(0, Math.round(completedCrop.y * scaleY));
  const sourceWidth = Math.max(
    1,
    Math.min(naturalWidth - sourceX, Math.round(completedCrop.width * scaleX)),
  );
  const sourceHeight = Math.max(
    1,
    Math.min(naturalHeight - sourceY, Math.round(completedCrop.height * scaleY)),
  );

  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = sourceWidth;
  outputCanvas.height = sourceHeight;

  const ctx = outputCanvas.getContext('2d', { alpha: true });

  if (!ctx) {
    throw new Error('No 2d context');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.clearRect(0, 0, sourceWidth, sourceHeight);

  // Crop directly from the original image. Do not redraw the preview canvas,
  // because that introduces an extra resampling pass and makes text/details softer.
  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    sourceWidth,
    sourceHeight,
  );

  return outputCanvas.toDataURL(mimeType, quality);
};

export const readFileAsImage = async (file: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const newImage = new Image();

    newImage.onload = () => resolve(newImage);
    newImage.onerror = () => reject(new Error('The selected image cannot be decoded by this browser'));
    newImage.src = file;
  });

export const checkFileType = (file, types) =>
  types.some((type) => {
    switch (type) {
      case 'image':
        return /^image\//.test(file?.type);
      case 'any':
        return true;
      default:
        return false;
    }
  });

const resizeImageResolution = async (file: File, fileName: string, quality = 1, maxWidth = 1440): Promise<File> => {
  const readFile = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = reader.result as string;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const image = await readFile(file);

  const originalWidth = image.width;
  const originalHeight = image.height;

  // –ö–∞—Ä—Ç–∏–Ω–∫–∞ –ø–æ—Å–ª–µ crop —É–∂–µ WebP –Ω—É–∂–Ω–æ–≥–æ –∫–∞—á–µ—Å—Ç–≤–∞.
  // –ï—Å–ª–∏ —É–º–µ–Ω—å—à–∞—Ç—å –µ—ë –Ω–µ –Ω–∞–¥–æ, –Ω–µ –ø–µ—Ä–µ–∂–∏–º–∞–µ–º –ø–æ–≤—Ç–æ—Ä–Ω–æ.
  if (originalWidth <= maxWidth && file.type === 'image/webp') {
    return file;
  }

  const newWidth = Math.min(maxWidth, originalWidth);
  const scale = newWidth / originalWidth;
  const newHeight = Math.round(originalHeight * scale);

  const canvas = document.createElement('canvas');

  canvas.width = newWidth;
  canvas.height = newHeight;

  const ctx = canvas.getContext('2d', { alpha: true });

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.clearRect(0, 0, newWidth, newHeight);
  ctx.drawImage(image, 0, 0, newWidth, newHeight);

  const outputQuality = file.type === 'image/webp' ? 1 : quality;
  const dataURL = canvas.toDataURL('image/webp', outputQuality);

  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/webp';
  const bstr = atob(arr[1]);

  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
};

export const SUPPORTED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'mp4',
  'm4v',
  'xls',
  'xlsx',
  'csv',
  'pdf',
  'txt',
  'doc',
  'docx',
  'ppt',
  'pptx',
];

export const uploadFiles = async (props) => {
  const trackedFiles: Blob[] = [];
  try {
    const {
      fields,
      setUploadingProgress,
      isSharedFilesMode,
      dispatch,
      entityId,
      lng,
      types,
      entity,
      typeParams = [],
      filePath = '',
    } = props;

    const filesToUpload = [];
    const filesMetaInfo = [];
    const filesResultPath = [];
    await Promise.all(
      types?.map(async (type, typeIndex) => {
        const { quality, maxWidth } = typeParams[typeIndex] ?? {
          quality: 1,
          maxWidth: MAX_MOBILE_SCREEN_SIZE,
        };

        const fileResults = pickDeep(fields, type, {
          keepCircular: true,
          condense: false,
        });
        const fileResultsPaths = paths(fileResults, { leavesOnly: false });

        await Promise.all(
          fileResultsPaths.map(async (fileResultPath, ind) => {
            let data = get(fields, fileResultPath);

            if (data && !isArray(data)) {
              data = [data];
            }

            if (data) {
              await Promise.all(
                data.map(async (data, index) => {
                  function isDataURL(s) {
                    return !!s?.match?.(isDataURL.regex);
                  }
                  isDataURL.regex =
                    /^\s*data:([a-z]+\/[a-z]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+,;=\-._~:@/?%\s]*\s*$/i;

                  const wasGeneratedDataURL = isDataURL(data?.path);

                  if (wasGeneratedDataURL) {
                    data.path = dataURLtoFile(data?.path, data?.name);
                  }

                  if (data?.status === 'new') {
                    const alias = get(fields, 'alias', '');
                    const sourceName = fields?.alias || data?.name || data?.path?.name || 'file';

                    const splitNameAndExtension = (fileName: string) => {
                      const lastDotIndex = fileName.lastIndexOf('.');

                      if (lastDotIndex <= 0) {
                        return { name: fileName, extension: '' };
                      }

                      const name = fileName.slice(0, lastDotIndex);
                      const extension = fileName.slice(lastDotIndex + 1).toLowerCase();

                      if (!SUPPORTED_EXTENSIONS.includes(extension)) {
                        // —ç—Ç–æ –ù–ï —Ä–∞—Å—à–∏—Ä–µ–Ω–∏–µ ‚Üí —Ç–æ—á–∫–∞ —á–∞—Å—Ç—å –∏–º–µ–Ω–∏
                        return { name: fileName, extension: '' };
                      }

                      return { name, extension };
                    };
                    const { name: baseName, extension } = splitNameAndExtension(sourceName);
                    const base = slugify(baseName, {
                      lowercase: true,
                      separator: '-',
                    });

                    const isAlreadyCroppedMainWebp =
                      type === 'image' && wasGeneratedDataURL && data?.path?.type === 'image/webp';

                    filesToUpload.push(
                      isAlreadyCroppedMainWebp
                        ? data.path
                        : data?.path?.type === 'image/jpeg' ||
                            data?.path?.type === 'image/png' ||
                            data?.path?.type === 'image/webp'
                          ? await resizeImageResolution(data?.path, base, quality, maxWidth)
                          : data?.path,
                    );

                    filesResultPath.push({
                      sourceFile: data.path,
                      fileResultPath:
                        type === 'image' || type === 'thumbnail' || type === 'avatar'
                          ? `${fileResultPath}`
                          : `${fileResultPath}[${index}]`,
                      name: base,
                      description: typeof data?.description === 'string' ? data.description.trim().slice(0, 34) : '',
                      interactionIcon: typeof data?.interactionIcon === 'string' ? data.interactionIcon : null,
                    });
                    filesMetaInfo.push({
                      fileName: base,
                      destination: `${filePath}/${type}`,
                    });
                  }
                }),
              );
            }
          }),
        );
      }),
    );

    if (filesToUpload.length > 0) {
      const chunkSize = UPLOAD_CHUNK_BYTES;
      if (filesToUpload.length > UPLOAD_PACKAGE_FILES) throw new Error('–ú–∞–∫—Å–∏–º—É–º 10 —Ñ–∞–π–ª—ñ–≤');
      const manifest = filesToUpload.map((file, i) => {
        const format = String(mimeTypes.extension(file.type) || file.name?.split('.').pop() || '').toLowerCase();
        if (file.size < 1 || file.size > uploadSizeLimit(format)) throw new Error(`–ó–∞–≤–µ–ª–∏–∫–∏–π –∞–±–æ –ø–æ—Ä–æ–∂–Ω—ñ–π —Ñ–∞–π–ª: ${file.name}`);
        return { fileName: filesMetaInfo[i].fileName, language: lng,
          destination: filesMetaInfo[i].destination, fileFormat: format, size: file.size };
      });
      const documentIndexes = manifest.flatMap((file, index) =>
        VIDEO_UPLOAD_FORMATS.includes(file.fileFormat) ? [] : [index]);
      if (documentIndexes.reduce((sum, index) => sum + manifest[index].size, 0) > UPLOAD_PACKAGE_BYTES) {
        throw new Error('–ú–∞–∫—Å–∏–º–∞–ª—å–Ω–∏–π —Ä–æ–∑–º—ñ—Ä –ø–∞–∫–µ—Ç—É ‚Äî 100 MB');
      }
      filesResultPath.forEach(({ sourceFile }) => {
        trackedFiles.push(sourceFile);
        setFileUploadProgress(sourceFile, 0, 'uploading');
      });
      const transport = await getUploadTransport();
      // Server permits one video per initialization request. A form may also
      // contain a cover/documents: initialize independent batches, retaining
      // server-side per-file limits, ownership checks and account quotas.
      const groups = [
        ...(documentIndexes.length ? [documentIndexes] : []),
        ...manifest.flatMap((file, index) =>
          VIDEO_UPLOAD_FORMATS.includes(file.fileFormat) ? [[index]] : []),
      ];
      const sessions: any[] = new Array(manifest.length);
      for (const indexes of groups) {
        const batch = indexes.map(index => manifest[index]);
        const initialized = transport === 'presigned' ? await initDirectBatch(batch) : await initUploadBatch(batch);
        indexes.forEach((index, position) => { sessions[index] = initialized[position]; });
      }
      const totalParts = filesToUpload.reduce((sum, file, index) =>
        sum + Math.ceil(file.size / (transport === 'presigned' ? directSessionChunkBytes(sessions[index]) : chunkSize)), 0);
      let uploadedParts = 0;
      const outcomes = await Promise.allSettled(filesToUpload.map(async (fileToUpload, i) => {
        const sourceFile = filesResultPath[i].sourceFile;
        try {
        if (transport === 'presigned') {
          const location = await uploadDirectFile(fileToUpload, sessions[i] as any, () => {
            uploadedParts++;
            setUploadingProgress?.(uploadedParts / totalParts);
          }, (bytes) => {
            setFileUploadProgress(sourceFile, bytes / fileToUpload.size * 100,
              bytes >= fileToUpload.size ? 'finalizing' : 'uploading');
          });
          setFileUploadProgress(sourceFile, 100, 'completed');
          set(fields, filesResultPath[i].fileResultPath, {
            path: decodeURIComponent(location), name: filesResultPath[i].name, type: fileToUpload.type,
            ...(filesResultPath[i].description && { description: filesResultPath[i].description }),
            ...(filesResultPath[i].interactionIcon && { interactionIcon: filesResultPath[i].interactionIcon }),
          });
          return;
        }
        const { uploadId, key } = sessions[i] as any;
        const totalChunks = Math.ceil(fileToUpload.size / chunkSize);
        const parts = [];
        for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
          const start = (chunkNumber - 1) * chunkSize;
          const end = Math.min(start + chunkSize, fileToUpload.size);
          const { fileName: _fileName, ...metadata } = filesMetaInfo[i];
          const action = await dispatch(uploadFile({
            files: { data: [fileToUpload.slice(start, end)] },
            filesMetaInfo: [{ ...metadata, chunkNumber, key, totalChunks,
              contentRange: `bytes ${start}-${end - 1}/${fileToUpload.size}`, uploadId }],
          }));
          // Keep the existing thunk payload contract; reject Redux and GraphQL errors.
          const result = action?.payload;
          if (action?.meta?.requestStatus === 'rejected' || result?.errors?.length ||
              !result?.data?.uploadFileMutation?.uploadId) {
            throw new Error(result?.errors?.[0]?.message || action?.error?.message || '–ù–µ –≤–¥–∞–ª–æ—Å—è –∑–∞–≤–∞–Ω—Ç–∞–∂–∏—Ç–∏ —á–∞—Å—Ç–∏–Ω—É —Ñ–∞–π–ª—É');
          }
          parts.push({ ETag: result.data.uploadFileMutation.uploadId, PartNumber: chunkNumber });
          uploadedParts++;
          setUploadingProgress?.(uploadedParts / totalParts);
          setFileUploadProgress(sourceFile, end / fileToUpload.size * 100,
            chunkNumber === totalChunks ? 'finalizing' : 'uploading');
        }
        const action = await dispatch(completeUploadFile({ fileName: key, uploadId, parts }));
        const result = action?.payload;
        if (action?.meta?.requestStatus === 'rejected' || result?.errors?.length ||
            !result?.data?.completeUploadFileMutation?.location) {
          throw new Error(result?.errors?.[0]?.message || action?.error?.message || '–ù–µ –≤–¥–∞–ª–æ—Å—è –∑–∞–≤–µ—Ä—à–∏—Ç–∏ –∑–∞–≤–∞–Ω—Ç–∞–∂–µ–Ω–Ω—è');
        }
        set(fields, filesResultPath[i].fileResultPath, {
          path: decodeURIComponent(result.data.completeUploadFileMutation.location),
          name: filesResultPath[i].name, type: fileToUpload.type,
          ...(filesResultPath[i].description && { description: filesResultPath[i].description }),
          ...(filesResultPath[i].interactionIcon && { interactionIcon: filesResultPath[i].interactionIcon }),
        });
        setFileUploadProgress(sourceFile, 100, 'completed');
        } catch (error) {
          setFileUploadProgress(sourceFile, getFileUploadProgress(sourceFile)?.percent ?? 0, 'error');
          throw error;
        }
      }));
      const failed = outcomes.find(result => result.status === 'rejected');
      if (failed?.status === 'rejected') throw failed.reason;
    }

    setUploadingProgress && setUploadingProgress(null);

    return fields;
  } catch (e) {
    trackedFiles.forEach(file => {
      const state = getFileUploadProgress(file);
      if (state?.phase !== 'completed') setFileUploadProgress(file, state?.percent ?? 0, 'error');
    });
    props.setUploadingProgress?.(null);
    throw e instanceof Error ? e : new Error(String(e));
  }
};

